<template>
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>จองส่งต่างจังหวัด (รถใหญ่)</v-card-title>
      <v-card-text>
        <div class="mb-2">
          <strong>{{ refLabel }}</strong> · {{ fields.name }} · จ.{{ province }}
          <div class="caption">{{ fields.address }}</div>
          <!-- The SO's real item count and its volume in CBM -- the units the
               รถใหญ่ต่างจังหวัด page uses. Volume ÷ 0.14 read as "0.9 ชิ้น". -->
          <div v-if="isStaff" class="caption">
            {{ figures.count || '-' }} ชิ้น · {{ cbm(figures.volume) }} CBM · ฿{{ money(fields.orderValue) }}
          </div>
        </div>

        <!-- Said as soon as the dialog opens, not after บันทึก is pressed:
             createUpOrder refuses a missing value (UP_NO_VALUE) either way. -->
        <v-alert v-if="noValue" dense type="error" class="mb-2">{{ noValueText }}</v-alert>
        <!-- ฿0 is booked like any other order; said so a real sale keyed at ฿0
             by mistake is noticed here rather than on the trip. -->
        <v-alert v-else-if="zeroValue" dense type="info" class="mb-2">
          SO มูลค่า ฿0 (งานบริการ / สินค้าตัวอย่าง){{ figures.volume ? '' : ' · ไม่ใช้พื้นที่บนรถ' }}
        </v-alert>

        <!-- The pool's figures, for an order going into the pool. Beside a chosen
             trip they read as that truck's load (0 / 7 CBM next to a trip already
             carrying 1.04), so they show only for เข้าคิวรอจัดรอบ. -->
        <template v-if="choice === 'pool'">
          <v-alert v-for="card in cards" :key="card.key" dense outlined type="info" class="mb-2">
            <strong>{{ card.label }}</strong>
            <template v-if="isStaff">
              — {{ card.own.count + card.shared.count }} ชิ้น ·
              {{ cbm(card.own.volume + card.shared.volume) }} / {{ cbm(cfg.capacityCbm) }} CBM ·
              ฿{{ money(card.own.value + card.shared.value) }} / ฿{{ money(cfg.minValue) }}
            </template>
            <template v-else> — {{ U.shopPanelStatus(card) }}</template>
            <group-provinces :group-key="card.key" />
          </v-alert>
        </template>

        <!-- A trip already fixed for this order's group takes it first, while the
             truck has room (U.checkJoinTrip, the same rule the save runs). A full
             one is shown เต็ม; the pool waits for a later trip. -->
        <v-radio-group v-if="choices.length || choice !== 'pool'" v-model="choice" class="mt-0">
          <v-radio v-for="c in choices" :key="c.trip.id" :value="c.trip.id" :disabled="!c.ok">
            <template v-slot:label>
              <span>ขึ้นรอบ {{ U.tripLabel(c.trip) }}</span>
              <span v-if="isStaff" class="caption ml-2">
                {{ cbm(c.volume) }} + {{ cbm(figures.volume) }} / {{ cbm(cfg.capacityCbm) }} CBM
              </span>
              <v-chip v-if="c.reason === 'UP_TRIP_FULL'" x-small color="error" class="ml-2">เต็ม</v-chip>
            </template>
          </v-radio>
          <v-radio value="pool" label="เข้าคิวรอจัดรอบ (เลือกสัปดาห์ที่ลูกค้าพร้อมรับ)" />
        </v-radio-group>
        <!-- The chosen trip filled or was sent while the dialog was open: said
             here, and บันทึก stays off until the booker picks again. -->
        <v-alert v-if="choiceInfo.problem" dense type="warning" class="mb-2">
          {{ U.choiceProblemText(choiceInfo.problem) }}
        </v-alert>

        <v-select v-if="isStaff" v-model="installMode" :items="modeItems" label="การติดตั้ง" outlined dense />
        <div v-if="pickedTrip" class="caption">วันส่ง: {{ U.tripLabel(pickedTrip) }}</div>
        <!-- No week to pick: a route can take weeks to fill and the delivery team
             gives the date, so a pooled order waits from the first week the
             Thursday rule allows. A week list read as the customer's choice. -->
        <div v-else class="caption">ฝ่ายจัดส่งจะแจ้งวันส่งเมื่อจัดรอบรถแล้ว (อาจใช้เวลา 1–2 สัปดาห์)</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="$emit('input', false)">ยกเลิก</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="save">บันทึก</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import U from '@/components/upcountry'
import RJ from '@/components/returnJob'
import groupProvinces from '@/components/groupProvinces'
import US from '@/components/upcountryService'
import R from '@/components/bookingRules'
import { pushBookDateToSap, bookDateFailedNote } from '@/components/sapBookDate'
import { reportError } from '@/components/report'

export default {
  name: 'upcountryBookingDialog',
  components: { groupProvinces },
  // fields: soFieldsFrom(...) plus soNo, orderValue, orderDate, user.
  props: { value: Boolean, fields: { type: Object, required: true }, province: { type: String, required: true } },
  data () {
    return { U, cfg: U.CONFIG_DEFAULTS, orders: [], installMode: 'unknown', saving: false, unsub: null,
      trips: [], choice: 'pool', preselected: false, unsubTrips: null,
      modeItems: [{ text: 'ยังไม่ทราบ', value: 'unknown' }, { text: 'ติดตั้ง', value: 'install' }, { text: 'ส่งอย่างเดียว', value: 'drop' }] }
  },
  computed: {
    isStaff () { return R.isStaffRole(this.$store.state.status) },
    groups () { return U.groupsOf(this.province) },
    cards () { return U.boardCards(this.orders, this.cfg).filter(c => this.groups.indexOf(c.key) !== -1) },
    figures () { return U.orderFigures(this.fields, this.cfg) },
    // SO, or SO · RT for an exchange.
    refLabel () { return RJ.refText(this.fields) },
    // A connection without the deliveryDB change sends no orderValue; ฿0 is a
    // real value (service visit, exhibition stock) and is booked.
    noValue () { return U.sapOrderValue(this.fields.orderValue) === null },
    zeroValue () { return U.sapOrderValue(this.fields.orderValue) === 0 },
    noValueText () { return R.errorMessage('UP_NO_VALUE') },
    // What the rules measure: this SO's province and figures.
    draft () { return Object.assign({}, this.fields, { upProvince: this.province }) },
    tripList () { return U.tripChoices(this.trips, this.draft, this.cfg, R.todayStr()) },
    choices () { return this.tripList.choices },
    // The chosen trip while it is still offered and has room. A trip that left
    // the list (marked ส่งแล้ว) or filled is null here, said under the choice,
    // AND blocks บันทึก below -- so the order is never pooled or moved to
    // another date behind the booker's back.
    choiceInfo () { return U.choiceState(this.choice, this.choices) },
    pickedTrip () { return this.choiceInfo.trip },
    canSave () {
      if (this.saving || this.noValue) return false
      return this.choice === 'pool' || !!this.pickedTrip
    }
  },
  async created () {
    try { this.cfg = await US.getUpConfig() } catch (e) { reportError('upcountry config', e) }
    this.unsub = US.subscribeOrders(rows => { this.orders = rows }, e => reportError('upcountry orders', e))
    // The earliest trip with room is preselected from the FIRST snapshot only.
    // Following later snapshots would move a choice nobody had touched -- to the
    // pool, or to a trip on another date -- after the customer was told one.
    this.unsubTrips = US.subscribeTrips(U.firstOfferedWeek(R.todayStr()), rows => {
      this.trips = rows
      if (!this.preselected) {
        this.preselected = true
        this.choice = this.tripList.preselect || 'pool'
      }
    }, e => reportError('upcountry trips', e))
  },
  beforeDestroy () {
    if (this.unsub) this.unsub()
    if (this.unsubTrips) this.unsubTrips()
  },
  methods: {
    cbm (n) { return (Number(n) || 0).toFixed(2) },
    money (n) { return Number(n || 0).toLocaleString('th-TH') },
    async save () {
      if (!this.canSave) return
      this.saving = true
      const trip = this.pickedTrip
      try {
        const order = Object.assign({}, this.fields, {
          upProvince: this.province,
          earliestWeek: trip ? U.mondayOf(trip.startDate) : U.firstOfferedWeek(R.todayStr()),
          installMode: this.installMode
        })
        const id = await US.createUpOrder(order, { role: this.$store.state.status, tripId: trip ? trip.id : null })
        const date = trip ? trip.startDate : null
        // Pooled: date is null and R.sapBookDate writes nothing yet. On a trip
        // the customer has a date now, and SAP must hear it like every other path.
        // It never throws: it returns the SOs SAP did not take, for the page to say.
        const failed = await pushBookDateToSap(this.$store.getters.webPath, { soNo: this.fields.soNo, rtNo: this.fields.rtNo || '', status: 'active', date })
        this.$emit('saved', id, date, bookDateFailedNote(failed))
        this.$emit('input', false)
      } catch (e) {
        reportError('upcountry createUpOrder', e)
        const why = U.actionMessage(e)
        this.$root.$message('บันทึกไม่สำเร็จ', [why], { type: 'alert', color: 'error', width: 420 })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
